import { z } from 'zod';
import { GitHostKind, GitLinkSubjectKind, GitLinkTargetKind, GitRule, GitRuleTrigger, matchesBranchScope, } from './git.js';
import { WorkflowCategory } from './project.js';
/**
 * Luật tự đổi trạng thái của tab Git — APM-GIT-h, docs/plan-git-integration.md §6.
 *
 * Phần **quyết định** ("sự kiện này có đổi trạng thái task không") là
 * `decideTransition` ở `git.ts` (APM-GIT-a). Tệp này mang ba thứ đứng quanh nó,
 * đều là hình dữ liệu và hàm thuần — web và API dùng chung một bản:
 *
 * - **Nguyên nhân** ghi vào event (`GitStatusCause`, §4.1): `task.status_changed`
 *   do luật gây ra mang `payload.meta.cause`, và dòng lịch sử đọc nó ra thành
 *   "Git chuyển sang Xong — MR !45 «Sửa đăng nhập» đã merge vào `main`".
 * - **Kiểm bộ luật** (`validateGitRules`, §6.3): lỗi chặn lưu và cảnh báo cho PM.
 * - **Màn cấu hình luật** (`GitRulesPreview`): thứ `GET …/git/rules/preview` trả.
 */
// ── Nguyên nhân của một lần đổi trạng thái (§4.1, §6.2) ─────────────────────
/** Tiêu đề MR chép vào event cắt ở chừng này — event giữ mãi, tiêu đề dài 500 ký tự thì không cần. */
export const GIT_CAUSE_TITLE_MAX = 200;
/**
 * `payload.meta.cause` của `task.status_changed` khi luật git đổi trạng thái.
 *
 * **Bản chụp lúc luật chạy**, tự đủ để đọc: bản sao git có thể bị dọn (gỡ kho,
 * thu hẹp tầm nhập) còn event thì ở lại — dòng lịch sử phải kể được chuyện mà
 * không tra bảng nào. Vì vậy mang cả tiêu đề, số, nhánh và loại máy chủ (để viết
 * `MR !45` hay `PR #45`).
 */
export const GitStatusCause = z.object({
    kind: z.literal('git'),
    repositoryId: z.uuid(),
    hostKind: GitHostKind,
    trigger: GitRuleTrigger,
    subjectKind: GitLinkSubjectKind,
    /** Tên nhánh · sha · số MR ở dạng chuỗi — khoá tự nhiên của `git_link`. */
    subjectRef: z.string(),
    /** Số MR / PR; `null` khi thứ gây ra là nhánh hoặc commit. */
    number: z.number().int().nullable(),
    /** Tiêu đề MR / PR lúc luật chạy; `null` khi là nhánh hoặc commit. */
    title: z.string().nullable(),
    /** MR ⇒ nhánh **đích**; nhánh ⇒ chính nó; commit ⇒ nhánh nó được đọc từ đó (không biết thì `null`). */
    branch: z.string().nullable(),
    webUrl: z.string().nullable(),
});
/**
 * Nguyên nhân git của một event, đọc từ `payload` — `null` khi event không do
 * luật git gây ra (hoặc `meta.cause` không đúng hình: một dòng lịch sử kể sai
 * còn tệ hơn một dòng kể chung chung).
 */
export function gitStatusCauseOf(payload) {
    if (payload === null || typeof payload !== 'object')
        return null;
    const meta = payload.meta;
    if (meta === null || typeof meta !== 'object')
        return null;
    const parsed = GitStatusCause.safeParse(meta.cause);
    return parsed.success ? parsed.data : null;
}
/**
 * Cách gọi một MR / PR theo máy chủ: `MR !45` (GitLab) · `PR #45` (GitHub).
 * Không dịch — đây là ký hiệu của chính máy chủ git, người dùng gõ y như vậy ở đó.
 */
export function gitChangeRequestLabel(hostKind, number) {
    return hostKind === 'gitlab' ? `MR !${number}` : `PR #${number}`;
}
// ── Kiểm bộ luật (§6.1, §6.3) ───────────────────────────────────────────────
/** Một cột của workflow ở mức luật cần. */
export const GitRuleStatusOption = z.object({
    id: z.uuid(),
    name: z.string(),
    category: WorkflowCategory,
    position: z.number().int(),
    color: z.string().nullable(),
    /**
     * Cột của riêng một sprint (APM-BOARD-a1); `null` = cột chung, hợp lệ ở mọi board.
     * Luật trỏ vào cột của sprint chỉ chạy được cho việc **trong sprint ấy**.
     */
    sprintId: z.uuid().nullable(),
    sprintName: z.string().nullable(),
});
export const GitRuleBranch = z.object({
    name: z.string(),
    isDefault: z.boolean(),
    isProtected: z.boolean(),
});
/** Trần nhánh mỗi kho mà màn cấu hình luật nhận về — mặc định trước, được bảo vệ kế, rồi mới nhất. */
export const GIT_RULE_BRANCHES_MAX = 300;
/** Một kho đang gắn và các nhánh **đã biết** của nó (từ `git_branch`, không gọi máy chủ git). */
export const GitRuleRepository = z.object({
    id: z.uuid(),
    fullPath: z.string(),
    hostKind: GitHostKind,
    defaultBranch: z.string().nullable(),
    branches: z.array(GitRuleBranch),
    /** Kho có nhiều nhánh hơn `GIT_RULE_BRANCHES_MAX` — danh sách đã cắt. */
    branchesTruncated: z.boolean(),
});
/**
 * Lỗi **chặn lưu**:
 * - `status_not_in_workflow` — `toStatusId` không phải một cột của dự án (gõ tay,
 *   hoặc cột đã bị xoá). Luật như vậy lúc chạy sẽ luôn rơi vào `status_missing`.
 * - `duplicate_rule` — hai luật cùng `trigger` + `branchScope`: hai câu trả lời
 *   cho cùng một sự kiện.
 */
export const GitRuleErrorCode = z.enum(['status_not_in_workflow', 'duplicate_rule']);
export const GitRuleError = z.object({
    code: GitRuleErrorCode,
    /** Chỉ số của luật trong mảng `rules`. */
    ruleIndex: z.number().int().nonnegative(),
});
/**
 * Cảnh báo — **không** chặn lưu, PM đọc rồi tự quyết (§6.3):
 * - `unprotected_branch` — luật `cr_merged` có phạm vi trỏ vào nhánh **không được
 *   bảo vệ**: ai có quyền push cũng tự merge được, tức tự hoàn thành task của mình
 *   không qua ai. Kèm `repositoryId` và tên các nhánh ấy.
 * - `sprint_points` — dự án có sprint và luật đưa task vào một cột nhóm `done`:
 *   task vào Xong mà không ai bấm, điểm của sprint đếm theo.
 * - `sprint_scoped_status` — cột đích thuộc riêng một sprint: việc ở ngoài sprint
 *   ấy không chuyển được (lúc chạy ghi `skipped`).
 */
export const GitRuleWarningCode = z.enum([
    'unprotected_branch',
    'sprint_points',
    'sprint_scoped_status',
]);
export const GitRuleWarning = z.object({
    code: GitRuleWarningCode,
    ruleIndex: z.number().int().nonnegative(),
    /** Chỉ với `unprotected_branch`. */
    repositoryId: z.uuid().optional(),
    /** Chỉ với `unprotected_branch`: nhánh thuộc phạm vi mà không được bảo vệ (≤ 5 tên). */
    branches: z.array(z.string()).optional(),
});
const WARNING_BRANCHES_MAX = 5;
/**
 * Kiểm một bộ luật với dự án nó sẽ chạy trên — hàm thuần, gọi được ở cả lệnh lưu
 * cấu hình (chặn theo `errors`) lẫn màn cấu hình (hiện `warnings` ngay khi PM sửa,
 * trước khi lưu).
 *
 * Cảnh báo tính cho **mọi** luật, kể cả luật đang tắt: PM cần thấy nó trước khi
 * bật, không phải sau.
 *
 * ⚠️ `unprotected_branch` chỉ nói về nhánh **đã kéo về**. Phạm vi là một mẫu
 * (`release/*`) chưa khớp nhánh nào thì không có gì để cảnh báo — và không có gì
 * đảm bảo nhánh `release/…` tạo sau này sẽ được bảo vệ.
 */
export function validateGitRules(rules, context) {
    const errors = [];
    const warnings = [];
    const statusById = new Map(context.statuses.map((status) => [status.id, status]));
    const seen = new Set();
    rules.forEach((rule, ruleIndex) => {
        const pair = `${rule.trigger}\u0000${rule.branchScope}`;
        if (seen.has(pair))
            errors.push({ code: 'duplicate_rule', ruleIndex });
        seen.add(pair);
        const target = statusById.get(rule.toStatusId);
        if (!target) {
            errors.push({ code: 'status_not_in_workflow', ruleIndex });
            return;
        }
        if (rule.trigger === 'cr_merged') {
            for (const repository of context.repositories) {
                const open = repository.branches.filter((branch) => matchesBranchScope(rule.branchScope, branch) && !branch.isProtected);
                if (open.length === 0)
                    continue;
                warnings.push({
                    code: 'unprotected_branch',
                    ruleIndex,
                    repositoryId: repository.id,
                    branches: open.slice(0, WARNING_BRANCHES_MAX).map((branch) => branch.name),
                });
            }
        }
        if (context.hasSprints && target.category === 'done') {
            warnings.push({ code: 'sprint_points', ruleIndex });
        }
        if (target.sprintId !== null)
            warnings.push({ code: 'sprint_scoped_status', ruleIndex });
    });
    return { errors, warnings };
}
// ── Màn cấu hình luật: GET …/git/rules/preview ──────────────────────────────
/** Kết cục của một lần luật được xét — cột `git_rule_fired.outcome`. */
export const GitRuleOutcome = z.enum(['applied', 'skipped']);
/**
 * Một dòng của sổ `git_rule_fired` — "luật đã chạy thế nào".
 *
 * `skipReason` là một `GitTransitionSkipReason` (`backward`, `pending_work`…) **hoặc**
 * mã lỗi của lệnh đổi trạng thái khi nó ném (`SPRINT_CLOSED_TASK`,
 * `CHECKLIST_INCOMPLETE`, `NOT_FOUND`…) — giao diện dịch được thì dịch, không thì
 * hiện nguyên mã.
 */
export const GitRuleFiredView = z.object({
    id: z.uuid(),
    firedAt: z.iso.datetime(),
    trigger: GitRuleTrigger,
    outcome: GitRuleOutcome,
    skipReason: z.string().nullable(),
    repositoryId: z.uuid(),
    target: z.object({
        kind: GitLinkTargetKind,
        id: z.uuid(),
        /** `GOLD-123`; `null` khi việc đã bị xoá. Chỉ mã — tên việc đọc ở màn của nó. */
        code: z.string().nullable(),
    }),
    subject: z.object({
        kind: GitLinkSubjectKind,
        /** Tên nhánh · sha · số MR ở dạng chuỗi. */
        ref: z.string(),
        /** Tiêu đề MR / PR khi còn trong bản sao. */
        title: z.string().nullable(),
        webUrl: z.string().nullable(),
    }),
});
/** Số dòng sổ gần nhất màn cấu hình hiện. */
export const GIT_RULE_FIRED_RECENT = 20;
/**
 * Mọi thứ màn cấu hình luật cần, một lời gọi (`git:manage`):
 *
 * - `rules` — bộ luật **đang lưu** (chỉ số trong `errors` / `warnings` trỏ vào đây).
 * - `statuses`, `repositories`, `hasSprints` — đúng hình `GitRuleCheckContext`, để
 *   web gọi lại `validateGitRules` trên bản PM đang sửa mà không hỏi server.
 * - `errors`, `warnings` — kết quả của `validateGitRules` trên `rules`. Bộ luật đã
 *   lưu vẫn có thể có `errors`: cột đích bị xoá **sau** khi lưu ⇒ luật "cần sửa".
 * - `recent` — `GIT_RULE_FIRED_RECENT` dòng sổ mới nhất, mới trước.
 *
 * ⚠️ Không nhận `asOf`: đây là màn **cấu hình** (trạng thái lúc này của luật,
 * workflow, nhánh), như màn Cài đặt kho. `recent` tự mang mốc `firedAt`.
 */
export const GitRulesPreview = z.object({
    rules: z.array(GitRule),
    statuses: z.array(GitRuleStatusOption),
    repositories: z.array(GitRuleRepository),
    hasSprints: z.boolean(),
    errors: z.array(GitRuleError),
    warnings: z.array(GitRuleWarning),
    recent: z.array(GitRuleFiredView),
});
//# sourceMappingURL=git-rules.js.map